var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useMemo, useState } from 'react';
import { useHistory } from 'react-router';
import styled from 'styled-components';
import { MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField } from '@material-ui/core';
import { ConfigFieldControl } from '@sierralabs/core-ui';
import SiteRoutePicker from '../../common/pickers/SiteRoutePicker';
import { useSiteRouteComponents } from '../../hooks/site-route/useSiteRouteComponents';
import { useSiteContext } from '../../SiteContext';
function isRouteParam(part) {
    return part.startsWith(':') && part.length > 1;
}
// transform route part :param and :param? to param
// (example route: /path/:entityId/:optional?)
function transformRoutePart(part) {
    return part.slice(+part.startsWith(':'), part.endsWith('?') ? -1 : undefined);
}
export function getRouteParams(routePath) {
    const params = [];
    for (const pathPart of routePath.split('/')) {
        const part = pathPart.trim();
        if (isRouteParam(part)) {
            const param = transformRoutePart(part);
            if (!params.includes(param)) {
                params.push(param);
            }
        }
    }
    return params;
}
const StyledConfiguratorDiv = styled.div `
  display: flex;
  flex-direction: column;
  width: 100%;
`;
const SiteEventRouteConfigurator = (_a) => {
    var { onChange } = _a, props = __rest(_a, ["onChange"]);
    let [config, setConfig] = useState(props.config || {});
    const routes = useSiteRouteComponents();
    const params = useMemo(() => {
        var _a;
        const route = routes.find(route => route.id === config.routeId);
        return getRouteParams(((_a = route === null || route === void 0 ? void 0 : route.props) === null || _a === void 0 ? void 0 : _a.path) || '');
    }, [config.routeId, routes]);
    return (React.createElement(StyledConfiguratorDiv, { className: 'event-route-configurator' },
        React.createElement(ConfigFieldControl, null,
            React.createElement(SiteRoutePicker, { variant: 'outlined', value: config === null || config === void 0 ? void 0 : config.routeId, onChange: route => {
                    if (route) {
                        const routeId = route.id;
                        setConfig(Object.assign(Object.assign({}, config), { routeId }));
                        onChange === null || onChange === void 0 ? void 0 : onChange(Object.assign(Object.assign({}, config), { routeId }));
                    }
                } })),
        Boolean(params.length) && (React.createElement(TableContainer, null,
            React.createElement(Table, null,
                React.createElement(TableHead, null,
                    React.createElement(TableRow, null,
                        React.createElement(TableCell, null, "Param Key"),
                        React.createElement(TableCell, null, "Source"),
                        React.createElement(TableCell, null, "Configuration"))),
                React.createElement(TableBody, null, params.map((paramKey, index) => {
                    var _a;
                    const param = (_a = config.routeParams) === null || _a === void 0 ? void 0 : _a[index];
                    const routeParam = (param === null || param === void 0 ? void 0 : param.key) === paramKey ? param : undefined;
                    return (React.createElement(SiteEventRouteParamConfigurator, { key: index, paramKey: paramKey, routeParam: routeParam, onChange: routeParam => {
                            if (!config.routeParams)
                                config.routeParams = [];
                            routeParam.key = paramKey;
                            config.routeParams[index] = routeParam;
                            setConfig(Object.assign({}, config));
                            onChange === null || onChange === void 0 ? void 0 : onChange(Object.assign({}, config));
                        } }));
                })))))));
};
export const SiteEventRouteParamConfigurator = (_a) => {
    var { paramKey, onChange } = _a, props = __rest(_a, ["paramKey", "onChange"]);
    const { paramPlugins } = useSiteContext();
    const [routeParam, setRouteParam] = useState(props.routeParam || {});
    const paramPlugin = routeParam.type
        ? paramPlugins.find(p => p.type === routeParam.type)
        : undefined;
    return (React.createElement(TableRow, null,
        React.createElement(TableCell, null, paramKey),
        React.createElement(TableCell, null,
            React.createElement(TextField, { select: true, variant: 'outlined', size: 'small', placeholder: 'Select Source', value: (routeParam === null || routeParam === void 0 ? void 0 : routeParam.type) || '', onChange: event => {
                    const updatedRouteParam = Object.assign(Object.assign({}, routeParam), { type: event.target.value });
                    setRouteParam(updatedRouteParam);
                    onChange === null || onChange === void 0 ? void 0 : onChange(updatedRouteParam);
                } }, paramPlugins
                .filter(p => p.type)
                .map(plugin => (React.createElement(MenuItem, { key: plugin.type, value: plugin.type }, plugin.name))))),
        React.createElement(TableCell, null, (paramPlugin === null || paramPlugin === void 0 ? void 0 : paramPlugin.configurator) && (React.createElement(paramPlugin.configurator, { config: routeParam === null || routeParam === void 0 ? void 0 : routeParam.config, onChange: config => {
                const updatedRouteParam = Object.assign(Object.assign({}, routeParam), { config });
                setRouteParam(updatedRouteParam);
                onChange === null || onChange === void 0 ? void 0 : onChange(updatedRouteParam);
            } })))));
};
export const SiteEventRoutePlugin = {
    name: 'Route Navigation',
    type: 'route-navigation',
    eventTypes: ['onClick'],
    configurator: SiteEventRouteConfigurator,
    getConfigSummary: event => {
        const ConfigSummary = () => {
            const routes = useSiteRouteComponents();
            const route = useMemo(() => {
                return routes.find(r => { var _a; return r.id === ((_a = event === null || event === void 0 ? void 0 : event.config) === null || _a === void 0 ? void 0 : _a.routeId); });
            }, [routes]);
            return React.createElement(React.Fragment, null,
                "Navigate to ", route === null || route === void 0 ? void 0 :
                route.name);
        };
        return React.createElement(ConfigSummary, null);
    },
    handler: (config, context, data) => {
        var _a;
        const { paramPlugins, routes, history } = context.hook;
        // console.log('event handler', config, context, data);
        const route = routes.find(r => r.id === config.routeId);
        let url = ((_a = route === null || route === void 0 ? void 0 : route.props) === null || _a === void 0 ? void 0 : _a.path) || '';
        for (const routeParam of config.routeParams || []) {
            const paramPlugin = paramPlugins.find(p => p.type === routeParam.type);
            const paramContext = context.params.find(p => p.type === (paramPlugin === null || paramPlugin === void 0 ? void 0 : paramPlugin.type));
            const value = paramPlugin === null || paramPlugin === void 0 ? void 0 : paramPlugin.getParamValue(routeParam.config, paramContext === null || paramContext === void 0 ? void 0 : paramContext.hook, data);
            url = url.replace(`:${routeParam.key}`, value);
        }
        // console.log('history push url', url);
        history.push(url);
    },
    useHook: () => {
        const history = useHistory();
        const { paramPlugins } = useSiteContext();
        const routes = useSiteRouteComponents();
        return { history, paramPlugins, routes };
    }
};
//# sourceMappingURL=SiteEventRoutePlugin.js.map